// Voxel-style ship + gull geometry for AmbientSea: boxes merged into two
// small BufferGeometries that share ONE material. Per-vertex `aPart` tells the
// shader what a vertex is:
//   0 hull (not tinted)   1 sail/flag (tinted per ship)   2 foam wake
//   3 gull body           4 gull wing (flaps)   5 stern lantern (emissive)
// Forward is +X. The ship is ~0.9 long; a tile is 1.73 across.

import * as THREE from 'three';

export const PART = {
  hull: 0,
  sail: 1,
  foam: 2,
  gullBody: 3,
  gullWing: 4,
  lantern: 5,
  /** Flag body: white canvas for merchants, black for pirates. */
  pennant: 6,
  /** The mark on the flag: white for pirates (a skull read as a smudge). */
  pennantMark: 7,
} as const;

interface Acc {
  pos: number[];
  nrm: number[];
  col: number[];
  part: number[];
  /** 0 = every variant (hull, castle); 1/2/3 = only that rig is drawn. */
  variant: number[];
  idx: number[];
}

const FACES: ReadonlyArray<{ n: [number, number, number]; v: Array<[number, number, number]> }> = [
  {
    n: [1, 0, 0],
    v: [
      [1, -1, 1],
      [1, -1, -1],
      [1, 1, -1],
      [1, 1, 1],
    ],
  },
  {
    n: [-1, 0, 0],
    v: [
      [-1, -1, -1],
      [-1, -1, 1],
      [-1, 1, 1],
      [-1, 1, -1],
    ],
  },
  {
    n: [0, 1, 0],
    v: [
      [-1, 1, 1],
      [1, 1, 1],
      [1, 1, -1],
      [-1, 1, -1],
    ],
  },
  {
    n: [0, -1, 0],
    v: [
      [-1, -1, -1],
      [1, -1, -1],
      [1, -1, 1],
      [-1, -1, 1],
    ],
  },
  {
    n: [0, 0, 1],
    v: [
      [-1, -1, 1],
      [1, -1, 1],
      [1, 1, 1],
      [-1, 1, 1],
    ],
  },
  {
    n: [0, 0, -1],
    v: [
      [1, -1, -1],
      [-1, -1, -1],
      [-1, 1, -1],
      [1, 1, -1],
    ],
  },
];

/** Append a (yaw-rotated) box centred at (x,y,z) with full size (w,h,d). */
function box(
  acc: Acc,
  [w, h, d]: readonly [number, number, number],
  [x, y, z]: readonly [number, number, number],
  color: string,
  part: number,
  yaw = 0,
  variant = 0,
): void {
  const c = new THREE.Color(color);
  const cs = Math.cos(yaw);
  const sn = Math.sin(yaw);
  for (const f of FACES) {
    const base = acc.pos.length / 3;
    for (const [vx, vy, vz] of f.v) {
      const lx = (vx * w) / 2;
      const lz = (vz * d) / 2;
      acc.pos.push(x + lx * cs + lz * sn, y + (vy * h) / 2, z - lx * sn + lz * cs);
      acc.nrm.push(f.n[0] * cs + f.n[2] * sn, f.n[1], -f.n[0] * sn + f.n[2] * cs);
      acc.col.push(c.r, c.g, c.b);
      acc.part.push(part);
      acc.variant.push(variant);
    }
    acc.idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }
}

function finish(acc: Acc): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(acc.pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(acc.nrm, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(acc.col, 3));
  g.setAttribute('aPart', new THREE.Float32BufferAttribute(acc.part, 1));
  g.setAttribute('aShipVariant', new THREE.Float32BufferAttribute(acc.variant, 1));
  g.setIndex(acc.idx);
  g.computeBoundingSphere();
  return g;
}

const newAcc = (): Acc => ({ pos: [], nrm: [], col: [], part: [], variant: [], idx: [] });

const HULL = '#6b4a2f';
const HULL_DARK = '#4f3622';
const DECK = '#8a6a44';
const MAST = '#4a3420';
const CANVAS = '#ffffff'; // tinted per ship in the shader

/**
 * Rig per ship variant: [mast x, mast height, sail scale]. Forward is +X.
 * Proportions ported from the Pirate Quest ship model (masts at 0.24L / -0.02L /
 * -0.30L, two tiers per mast), but with **narrower sails**: that model is seen
 * from a low chase camera, while ours is a near-top-down board view, where full
 * 0.36L-wide courses on adjacent masts merge into one slab. Hull length 1.15
 * (a hex is 1.73) gives the masts room to read apart (owner's note, 2026-10-22).
 */
const RIGS: Readonly<Record<1 | 2 | 3, ReadonlyArray<readonly [number, number, number]>>> = {
  1: [[0.05, 1.02, 1.12]],
  2: [
    [0.21, 0.88, 0.92],
    [-0.18, 0.94, 1.0],
  ],
  3: [
    [0.28, 0.84, 0.86],
    [-0.02, 0.98, 1.0],
    [-0.35, 0.76, 0.7],
  ],
};

/** Deck top: the foot of every mast. */
const MAST_BASE = 0.18;
/** Sail scale: the Pirate Quest rig at our hull length (their hull is 7.4). */
const RIG_SCALE = 0.1554;

/** One square-rigged mast: the spar, a course and a topsail, each on a yard. */
function mastRig(a: Acc, x: number, h: number, s: number, variant: number): void {
  box(a, [0.03, h, 0.03], [x, MAST_BASE + h / 2, 0], MAST, PART.hull, 0, variant);
  const tiers: ReadonlyArray<readonly [number, number, number]> = [
    [2.15 * s, 1.6 * s, 0.42],
    [1.7 * s, 1.2 * s, 0.72],
  ];
  tiers.forEach(([w, sh, at], i) => {
    const width = w * RIG_SCALE;
    const height = sh * RIG_SCALE;
    const y = MAST_BASE + h * at;
    // Yard across the beam, then the sail hanging from it.
    box(a, [0.02, 0.02, width + 0.04], [x, y + height / 2, 0], MAST, PART.hull, 0, variant);
    box(a, [0.018, height, width], [x + 0.016, y, 0], CANVAS, PART.sail, 0, variant);
    // The topsail is a shade darker so the two tiers read apart at distance.
    if (i === 1)
      box(a, [0.019, height, width], [x + 0.016, y, 0], '#e2e2e2', PART.sail, 0, variant);
  });
}

/** A lateen sail on the mizzen: one raked spar and a triangular-ish sheet. */
function lateenRig(a: Acc, x: number, h: number, s: number, variant: number): void {
  box(a, [0.03, h, 0.03], [x, MAST_BASE + h / 2, 0], MAST, PART.hull, 0, variant);
  const w = 1.7 * s * RIG_SCALE;
  const sh = 1.6 * s * RIG_SCALE;
  box(a, [0.018, sh, w], [x + 0.016, MAST_BASE + h * 0.34, 0], CANVAS, PART.sail, 0.42, variant);
  box(a, [0.018, 0.018, w + 0.04], [x, MAST_BASE + h * 0.42, 0], MAST, PART.hull, 0.42, variant);
}

/**
 * The hull, shared by every variant: sheer bow, raised stern castle, a low
 * forecastle, deck, gold wale stripe and gunports along both sides, the stern
 * lantern and a bowsprit — the detail that sells a hull at board distance.
 */
function hull(a: Acc): void {
  box(a, [1.15, 0.16, 0.38], [0, 0.08, 0], HULL, PART.hull);
  box(a, [0.26, 0.11, 0.24], [0.68, 0.1, 0], HULL_DARK, PART.hull); // bow
  box(a, [0.14, 0.06, 0.12], [0.84, 0.09, 0], HULL_DARK, PART.hull); // stem
  box(a, [0.3, 0.13, 0.4], [-0.46, 0.245, 0], HULL_DARK, PART.hull); // stern castle
  box(a, [0.2, 0.08, 0.28], [0.5, 0.2, 0], HULL_DARK, PART.hull); // forecastle
  box(a, [0.62, 0.02, 0.3], [0.06, 0.17, 0], DECK, PART.hull); // deck
  box(a, [0.3, 0.02, 0.42], [-0.46, 0.31, 0], '#c9a24a', PART.hull); // castle rail
  // Gold wale stripe and gunports down both sides.
  for (const zSide of [-1, 1] as const) {
    box(a, [0.86, 0.025, 0.02], [0.02, 0.125, zSide * 0.196], '#c9a24a', PART.hull);
    for (const gx of [-0.05, 0.13, 0.31]) {
      box(a, [0.055, 0.045, 0.02], [gx, 0.09, zSide * 0.2], '#2a2420', PART.hull);
    }
  }
  box(a, [0.06, 0.06, 0.06], [-0.52, 0.36, 0], '#ffd27a', PART.lantern); // stern lantern
  box(a, [0.26, 0.026, 0.026], [0.93, 0.25, 0], MAST, PART.hull); // bowsprit
}

/**
 * Every variant's rig lives in ONE geometry: a per-vertex `aShipVariant` tags
 * which rig a box belongs to (0 = the shared hull), and the shader collapses the
 * parts that do not match the instance's variant. One draw call, no per-variant
 * mesh bookkeeping.
 */
export function buildShipGeometry(): THREE.BufferGeometry {
  const a = newAcc();
  hull(a);
  for (const v of [1, 2, 3] as const) {
    const rig = RIGS[v];
    rig.forEach(([x, h, s], i) => {
      // The three-master's last mast is lateen-rigged, like a galleon's mizzen.
      if (v === 3 && i === 2) lateenRig(a, x, h, s, v);
      else mastRig(a, x, h, s, v);
    });
    // Pennant at the tallest truck of this rig.
    const tallest = rig.reduce((m, r) => (r[1] > m[1] ? r : m));
    const truck = MAST_BASE + tallest[1] + 0.04;
    box(a, [0.12, 0.04, 0.015], [tallest[0], truck, 0], CANVAS, PART.pennant, 0, v);
    // A mark on the flag. At board distance a skull is a smudge, so it is a
    // white bar: black-and-white is what reads as "pirate" (owner's note).
    box(a, [0.05, 0.014, 0.017], [tallest[0], truck, 0], CANVAS, PART.pennantMark, 0, v);
  }
  return finish(a);
}

/** A gull: slim body, two swept-back wings with dark tips (the shader flaps them). */
export function buildGullGeometry(): THREE.BufferGeometry {
  const a = newAcc();
  box(a, [0.11, 0.03, 0.045], [0, 0, 0], '#f4f4f0', PART.gullBody);
  box(a, [0.04, 0.025, 0.03], [0.07, 0.005, 0], '#e8a43a', PART.gullBody); // beak
  // The wings sweep back, so from the game's top-down camera the bird reads as
  // a gull rather than as a pin stuck in the sand (the real-GPU pass,
  // 2026-10-22). A negative yaw swings the +z wing's tip backwards.
  box(a, [0.15, 0.008, 0.11], [-0.02, 0.005, 0.085], '#fafafa', PART.gullWing, -0.5);
  box(a, [0.15, 0.008, 0.11], [-0.02, 0.005, -0.085], '#fafafa', PART.gullWing, 0.5);
  box(a, [0.05, 0.008, 0.05], [-0.05, 0.005, 0.14], '#3a3a3e', PART.gullWing, -0.5); // wing tips
  box(a, [0.05, 0.008, 0.05], [-0.05, 0.005, -0.14], '#3a3a3e', PART.gullWing, 0.5); // wing tips
  return finish(a);
}
